import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, RotateCcw, Undo2 } from 'lucide-react';
import { formatIndicatorDisplay } from '../../utils/indicatorCategories';
import {
  getEffectiveSettings,
  isCustomized,
  countCustomized,
  hasLastCustom,
} from '../../utils/timeframeIndicatorSettings';

/**
 * Shows whether the active indicators are on this timeframe's defaults or have
 * been customized, and lets the user flip either way.
 *
 * Indicator settings are stored per timeframe, so this also serves as the
 * reminder that what you tune on the 1D chart doesn't follow you to 1Y.
 */
export default function IndicatorDefaultsBadge({
  indicators = [],
  timeframe = '3m',
  byTimeframe = {},
  lastCustom = {},
  onResetIndicator,
  onRestoreIndicator,
  onResetAll,
  onRestoreAll,
  align = 'right',
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const customCount = countCustomized(indicators, timeframe, byTimeframe);
  const restorableCount = indicators.filter(
    ind => !isCustomized(ind, timeframe, byTimeframe) && hasLastCustom(ind, timeframe, lastCustom)
  ).length;
  const label = timeframe.toUpperCase();

  if (indicators.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        title={`Indicator settings for ${label} — saved per timeframe, shared across tickers`}
        className={`text-xs px-2 py-1 rounded transition flex items-center gap-1 border ${
          customCount > 0
            ? 'bg-blue-600/15 text-blue-400 border-blue-600/30 hover:bg-blue-600/25'
            : 'bg-gray-700 text-gray-400 border-gray-600 hover:bg-gray-600'
        }`}
      >
        <span>{customCount > 0 ? `${customCount} custom` : 'Default'}</span>
        <ChevronDown className="w-3 h-3" />
      </button>

      {isOpen && (
        <div
          className={`absolute top-full ${align === 'right' ? 'right-0' : 'left-0'} mt-1 bg-gray-800 border border-gray-700 rounded shadow-lg z-50 min-w-72 max-h-80 overflow-y-auto`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-gray-700 flex items-center justify-between gap-2">
            <span className="text-xs text-gray-400 uppercase tracking-wide">
              Settings for {label}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={customCount === 0}
                onClick={() => onResetAll && onResetAll()}
                className="text-[11px] text-gray-400 hover:text-white disabled:opacity-40 disabled:hover:text-gray-400"
              >
                Reset all
              </button>
              <button
                type="button"
                disabled={restorableCount === 0}
                onClick={() => onRestoreAll && onRestoreAll()}
                className="text-[11px] text-blue-400 hover:text-blue-300 disabled:opacity-40 disabled:hover:text-blue-400"
              >
                Restore all
              </button>
            </div>
          </div>

          <div className="px-3 py-2 space-y-1">
            {indicators.map((ind) => {
              const custom = isCustomized(ind, timeframe, byTimeframe);
              const restorable = !custom && hasLastCustom(ind, timeframe, lastCustom);
              const settings = getEffectiveSettings(ind, timeframe, byTimeframe);
              return (
                <div key={ind} className="flex items-center justify-between gap-2 text-sm text-gray-200 px-1 py-1 rounded hover:bg-gray-700/50">
                  <span className="truncate">{formatIndicatorDisplay(ind, settings)}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                      custom ? 'bg-blue-600/20 text-blue-400' : 'bg-gray-700/50 text-gray-500'
                    }`}>
                      {custom ? 'Custom' : 'Default'}
                    </span>
                    {custom && (
                      <button
                        type="button"
                        onClick={() => onResetIndicator && onResetIndicator(ind)}
                        title="Back to this timeframe's default"
                        className="text-gray-400 hover:text-white"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {restorable && (
                      <button
                        type="button"
                        onClick={() => onRestoreIndicator && onRestoreIndicator(ind)}
                        title="Restore your last saved custom settings"
                        className="text-blue-400 hover:text-blue-300"
                      >
                        <Undo2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-3 py-2 border-t border-gray-700">
            <p className="text-[10px] text-gray-500">
              Saved per timeframe and shared across all tickers.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
